import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import days from 'dayjs';

import { environment } from '../../../environments/environment';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { Attach, C_value, Cuf, Event, Partner } from '@app/shared/_models/common';
import { DeliveryPlanning } from '../logistic.service';
import { ASKOP_AFTER, ASKOP_BEFORE, ASKOP_EXACT, C_COMPONENT_ADDRESS, CUF_ADDRESS_LAT, CUF_ADDRESS_LNG, OBJECTTYP_DELRUN, ObjectType, OrderLineType, SALES_ORDER_MODE_ASAP_DELIVERY, jsonheader, sales_order_mode } from '@app/shared/constants';
import { GeoService, GeoQuery } from '@app/shared/geo.service';
import { orderLineNamed, rentalSubtitle, rentalTitle } from '@app/sales/rental-billing/rental-billing.labels';
import { businessDayOf, pickedDateStr, wallMinutesOf } from '@app/shared/time';
import { AskWindow, AskWindowKind } from './delrun-schedule';

// ============================================================
// Modèles tournées (table del_run)
// ============================================================

// Une tournée de livraison : un véhicule, des livreurs, des livraisons ordonnées (delivery.seq_run).
export class DelRun {
	id: number = 0;
	seq: number = 1;								// n° de tournée dans la journée
	workers: number[] = [];					// ids utilisateurs (TODO : hr_worker quand la table existera côté back)
	vehicle: number | null = null;	// id c_values C_VALUES_VEHICLE (TODO : c_vehicle quand la table existera)
	dat: string = '';								// YYYY-MM-DD
	slot: number | null = null;			// créneau jour c_values 8 : 1 = Matin, 2 = Après-midi, null = journée
	start_slot: number | null = null;	// heure PRÉVUE de départ = id créneau horaire c_values 8
	depot: number | null = null;		// établissement de départ = id c_store
	realdatstart?: string | null = null;	// heure EFFECTIVE de départ (timestamptz ISO, stampée au démarrage réel) — v2
}

// Payload de l'event DELRUN : la tournée + ses livraisons ordonnées.
// Contrat back : INSERT/UPDATE écrit del_run (dont start_slot = heure prévue de départ, depot =
// établissement de départ) puis resynchronise delivery.id_del_run / seq_run / duration / plandatdel /
// lockdatdel ; DELETE libère les livraisons puis supprime.
//
// Deux temps DISTINCTS voyagent par livraison (voir delrun-schedule.ts) :
//   lockdatdel = la CONTRAINTE du planificateur (heure verrouillée à la main) — propriété de la
//                LIVRAISON : conservée quand elle quitte la tournée, c'est une exigence client ;
//   plandatdel = l'ENGAGEMENT figé à la validation (heure retenue, contrainte ou calculée) —
//                propriété de la PLANIFICATION : effacé par le back quand la livraison est libérée.
// realdatstart (heure effective) n'est PAS envoyé ici : il sera stampé par le back (event futur).
export interface DelRunPayload extends DelRun {
	lnk_deliveries: { id: number; seq_run: number; duration: number | null; plandatdel: string | null; lockdatdel: string | null }[];
}

// Ligne d'un BL prête à afficher dans l'aperçu (panneau survol/épinglé du board)
export interface BlPreviewLine {
	name: string;						// intitulé du contrat de location pour une ligne louée, comme le BL imprimé ; l'article sinon
	article: string | null;			// l'article sous l'intitulé du contrat ; null sans contrat
	qty: number;							// qté du BL
	ordered: number | null;	// qté de la ligne de commande (null si ligne orpheline)
	partial: boolean;					// qté BL ≠ qté commandée (livraison partielle)
	orphan: boolean;					// id_order_l absent des lignes de la commande (défensif)
	typ: OrderLineType | null;	// sales_order_l.typ : flèche des seules lignes louées (null si ligne orpheline)
	reprise: boolean;					// quantité négative sur le BL : reprise du matériel (flèche ←)
}

// Contenu d'un BL pour l'aperçu : lignes + position du BL dans la commande (reliquats)
export interface BlPreview {
	deliveryId: number;
	index: number;						// position 1-based du BL dans la commande (query param index = index - 1)
	count: number;						// nb de BL de la commande
	signed: boolean;
	realdatdel: string | null;
	lines: BlPreviewLine[];
}

export interface GeoPoint { lat: number; lon: number; }

// Temps de trajet d'une tournée (Géoplateforme itinéraire) : total + un tronçon par livraison.
// Les HEURES d'arrivée n'appartiennent pas à cet objet : elles sont calculées par computeSchedule
// (delrun-schedule.ts) et vivent dans RunVM.schedule — le trajet est une donnée, l'horaire un calcul.
export interface RouteVM {
	pending: boolean;
	duration: number | null;		// secondes (null = calcul impossible / indisponible)
	distance: number | null;		// mètres
	legs: (number | null)[];		// legs[i] = durée VERS la livraison i (i = 0 : depuis le dépôt) ; null = coords manquantes
	missing: number;						// points sans coordonnées, dépôt compris (exclus du calcul)
}

// v4 : entrées datées { t, pt } avec TTL. Les versions antérieures sont purgées au démarrage du
// service. v3 EST PÉRIMÉE PAR CONSTRUCTION : ses points ont été calculés par l'ancienne requête, qui
// ignorait street2 — les garder ferait survivre l'erreur jusqu'à 30 jours après le correctif.
export const GEOCACHE_KEY = 'mercalink.geocache.v4';
export const GEOCACHE_TTL_MS = 30 * 24 * 3600 * 1000;		// 30 jours
const GEOCACHE_LEGACY_KEYS = ['mercalink.geocache.v1', 'mercalink.geocache.v2', 'mercalink.geocache.v3'];
type GeocacheEntry = { t: number; pt: GeoPoint | null };

@Injectable({
	providedIn: 'root'
})
export class DelrunsService {

	private http = inject(HttpClient);
	private loginService = inject(LoginService);
	private geoService = inject(GeoService);
	private commonService = inject(CommonService);

	// Structure des cufs d'ADRESSE déclarée par la base (c_values 12, id_component C_COMPONENT_ADDRESS),
	// triée par seq. AUCUN id n'est cité en dur ici : « Niveau » et « Code porte » ne s'affichaient dans
	// les panneaux que parce que CETTE base les déclare sur les adresses — une autre base en aura d'autres
	// (ascenseur, digicode, étage…) ou aucun, et le panneau doit suivre sans qu'on touche au code.
	// Même source que la fiche tiers (PartnerComponent), qui les rend déjà génériquement.
	private addressCufs: Cuf[] = [];

	constructor() {
		try { GEOCACHE_LEGACY_KEYS.forEach((k) => localStorage.removeItem(k)); } catch { /* stockage indisponible */ }
		// Flux caché de ValuesService : ne complète jamais, l'abonnement vit autant que le service (root).
		this.commonService.getCufs(C_COMPONENT_ADDRESS).subscribe({
			next: (cufs) => {
				this.addressCufs = cufs
					.filter((c) => c.data?.table === 'c_partner')		// le n° de composant n'est pas qualifié par table : on l'exige
					.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0) || (a.id ?? 0) - (b.id ?? 0));
			},
			error: () => { this.addressCufs = []; },		// référentiel indisponible : l'adresse postale reste affichée
		});
	}

	// ============================================================
	// API
	// ============================================================

	// Tournées d'une date (jour du sélecteur, même convention que getPlanedDeliveriesByDate)
	getDelRunsByDate(dat: Date): Observable<DelRun[]> {
		const datstr = pickedDateStr(dat);
		return this.http.get<DelRun[]>(environment.server_url + '/del_runs?q=dat:' + datstr);
	}

	// INSERT / UPDATE / DELETE d'une tournée via le système d'events (objecttyp DELRUN).
	// L'INSERT renvoie l'id créé dans response.data.id (même convention que les autres objets).
	postDelRunEvent(eventtyp: number, payload: DelRunPayload): Observable<any> {
		const event: Event = {
			id: 0,
			typ: eventtyp,
			userid: this.loginService.loggeduser?.id,
			objectid: payload.id,
			objecttyp: OBJECTTYP_DELRUN,
			data: payload,
		};
		return this.http.post<any>(environment.server_url + '/event', event, { headers: jsonheader });
	}

	// Les BL de ces tournées en UN PDF, tournée après tournée (GET pur : delivery/src/routes.rs get_del_runs_bl) ; `ids` = ce que montre l'écran, 409 si la base diffère.
	getRunsBl$(runs: { id: number; ids: number[] }[]): Observable<Blob> {
		const param = runs.map((r) => r.id + ':' + r.ids.join(',')).join(';');
		return this.http.get(environment.server_url + '/del_runs/bl?runs=' + encodeURIComponent(param), { responseType: 'blob' });
	}

	// Un seul BL en PDF, signé tel qu'archivé sinon rendu du jour (GET pur : delivery/src/routes.rs get_delivery_bl) ; imprimante de l'aperçu d'une vignette.
	getDeliveryBl$(id: number): Observable<Blob> {
		return this.http.get(environment.server_url + '/deliveries/' + id + '/bl', { responseType: 'blob' });
	}

	// Commande + tous ses BL (lignes comprises) pour l'aperçu BL — même endpoint et mêmes options que
	// DeliveryComponent : « attachs » ramène les pièces jointes DE CHAQUE BL, dont le mémo éditable du
	// panneau (attach is_default_memo — voir memoAttachOf).
	// La réponse est un tableau à un élément (convention getQuotationById). Cache par commande, partagé
	// entre les cartes ; vidé par le board à chaque load(). Une erreur est évincée du cache (retry possible).
	private blOrderCache = new Map<number, Observable<any>>();

	getOrderWithBls$(idOrder: number): Observable<any> {
		let obs$ = this.blOrderCache.get(idOrder);
		if (!obs$) {
			obs$ = this.http.get<any>(environment.server_url + '/sales/order/' + idOrder + '?o=deliveries,attachs').pipe(
				map((orders: any) => (Array.isArray(orders) ? orders[0] : orders) ?? null),
				shareReplay(1),
				catchError((err) => {
					this.blOrderCache.delete(idOrder);
					return throwError(() => err);
				}),
			);
			this.blOrderCache.set(idOrder, obs$);
		}
		return obs$;
	}

	clearBlCache(): void {
		this.blOrderCache.clear();
	}

	readonly blEdited$ = new Subject<void>();		// BL de location enregistré ou supprimé depuis une carte : le board relit sa journée

	// ============================================================
	// Géocodage (carte) — BAN (GeoService) avec cache localStorage
	// ============================================================

	// Adresse -> {lat, lon} | null. Cache localStorage avec TTL (y compris les adresses introuvables,
	// pour ne pas re-marteler l'API) ; les erreurs réseau/timeout ne sont PAS mises en cache ; une
	// entrée expirée est regéocodée (et réécrite). BAN : précis au numéro en France, ~50 req/s.
	geocode$(query: GeoQuery): Observable<GeoPoint | null> {
		const key = (query?.label ?? '').trim().toLowerCase();
		if (!key) return of(null);
		const entry = DelrunsService.geocacheRead()[key];
		if (entry && Date.now() - entry.t < GEOCACHE_TTL_MS) return of(entry.pt);
		return this.geoService.geocodeRaw$(query).pipe(
			map((geo) => (geo ? { lat: geo.lat, lon: geo.lon } : null)),
			tap((pt) => DelrunsService.geocacheWrite(key, pt)),
			catchError(() => of(null)),		// après le tap : une erreur réseau n'est pas mise en cache
		);
	}

	private static geocacheRead(): Record<string, GeocacheEntry> {
		try { return JSON.parse(localStorage.getItem(GEOCACHE_KEY) ?? '{}'); } catch { return {}; }
	}

	private static geocacheWrite(key: string, pt: GeoPoint | null): void {
		try {
			const cache = DelrunsService.geocacheRead();
			cache[key] = { t: Date.now(), pt };
			localStorage.setItem(GEOCACHE_KEY, JSON.stringify(cache));
		} catch { /* quota localStorage plein : on géocodera à nouveau */ }
	}

	// ============================================================
	// Helpers purs (statiques : testables sans TestBed)
	// ============================================================

	// Résout le créneau « jour » d'un askslot : 0 = aucun, 1 = Matin, 2 = Après-midi.
	// Les créneaux horaires (id > 2) portent leur parent dans data.parent (c_values 8).
	// Lien d'un BL vers le board (règle de « Voir au planning ») : jour demandé et demi-journée ; null pour Emportée, Retrait magasin ou sans date.
	static boardLinkOf(day: string | null | undefined, askslot: number | null | undefined, mode: number | null | undefined, slots: C_value[]): string[] | null {
		if ((Number(mode) || 0) < SALES_ORDER_MODE_ASAP_DELIVERY || !day) return null;
		const half = DelrunsService.slotDayOf(askslot, slots);
		return ['/logistic/delruns', day, ...(half === 1 ? ['matin'] : half === 2 ? ['apres-midi'] : [])];
	}

	static slotDayOf(askslot: number | null | undefined, slots: C_value[]): number {
		const slot = Number(askslot) || 0;
		if (slot === 0) return 0;
		if (slot < 3) return slot;
		const found = (slots ?? []).find((s: any) => s.id === slot);
		return found?.data?.parent ?? 0;
	}

	// Filtre 3 états du board : null = tout voir ; 1|2 = créneau correspondant.
	// Une livraison sans créneau résolu (ASAP…) reste visible quel que soit le filtre.
	static matchesSlotFilter(d: DeliveryPlanning, filter: number | null, slots: C_value[]): boolean {
		if (!filter) return true;
		const day = DelrunsService.slotDayOf(d.askslot, slots);
		return day === 0 || day === filter;
	}

	// Créneaux horaires (c_values 8) proposés comme HEURE DE DÉPART d'une tournée, filtrés selon le
	// créneau de la tournée : Matin (1) => créneaux du matin, Après-midi (2) => ceux de l'après-midi,
	// Journée (null/0) => tous. Un créneau est retenu s'il est actif (is_active) OU marqué départ
	// supplémentaire (data.start_run = true : ex. 8h/13h, jamais proposés aux clients).
	static hourSlotsFor(slots: C_value[], slot: number | null | undefined): C_value[] {
		const day = (slot === 1 || slot === 2) ? slot : null;
		return (slots ?? []).filter((s: any) => {
			const parent = s.data?.parent;
			if (parent !== 1 && parent !== 2) return false;					// seulement les créneaux horaires
			if (day && parent !== day) return false;								// filtré par le créneau de la tournée
			return s.is_active !== false || s.data?.start_run === true;
		});
	}

	// Rappel du souhait client (logique mutualisée depuis PlanningComponent.getOpAndSlot) :
	// « Livraison dès que possible », « Début de matinée », « Avant 9h », « 11/07 »…
	static opAndSlotLabel(d: { mode: number; askslot: number | null; askop: number | null; askdatdel: string | null }, ops: C_value[], slots: C_value[]): string {
		if (!d || !d.mode || d.mode === 3) return 'Livraison dès que possible';
		if (d.mode < 3) return sales_order_mode.get(d.mode) ?? '';		// 1 Emportée / 2 Retrait magasin (hors tournée, défensif)
		let retour = '';
		if (d.askop) {
			retour = ((ops ?? []).find((op: any) => op.id == d.askop)?.name ?? '') + ' ';
		}
		if (d.askslot) {
			retour += (slots ?? []).find((slot: any) => slot.id == d.askslot)?.name ?? '';
			retour = retour.replace(' Matin', ' de matinée');
			retour = retour.replace(' Après-midi', " d'après-midi");
		}
		if (!retour.trim()) retour = d.askdatdel ? days(d.askdatdel).format('DD/MM') : '';
		return retour.trim();
	}

	// Contenu d'un BL à partir de sa commande chargée (?o=deliveries) : jointure lignes BL ↔ lignes commande.
	// Défensif : une ligne BL dont id_order_l n'existe plus dans la commande est affichée « Ligne #id » (orphan).
	// null si le BL n'appartient pas (ou plus) à la commande.
	static blPreviewOf(order: any, deliveryId: number): BlPreview | null {
		const dels: any[] = order?.lnk_deliveries ?? [];
		const idx = dels.findIndex((d: any) => +d?.id === +deliveryId);
		if (idx < 0) return null;
		const olines: any[] = order?.lines ?? [];
		const lines: BlPreviewLine[] = (dels[idx].lnk_deliveryl ?? []).map((dl: any) => {
			const ol = olines.find((l: any) => +l.id === +dl.id_order_l);
			const ordered = ol ? +ol.qty : null;
			const reprise = +dl.qty < 0;	// reprise d'un matériel loué : −1 sur sa ligne (JP 29/10), montrée 1 avec la flèche ←
			return {
				name: (ol && rentalTitle(orderLineNamed(ol))) ?? 'Ligne #' + dl.id_order_l,
				article: ol ? rentalSubtitle(orderLineNamed(ol)) : null,
				qty: Math.abs(+dl.qty),
				ordered,
				partial: !reprise && ordered !== null && +dl.qty !== ordered,
				orphan: !ol,
				typ: ol?.typ ?? null,
				reprise,
			};
		});
		return {
			deliveryId: +deliveryId,
			index: idx + 1,
			count: dels.length,
			signed: !!dels[idx].realdatdel,
			realdatdel: dels[idx].realdatdel ?? null,
			lines,
		};
	}

	// Attach « mémo par défaut » d'un BL (data.flags porte is_default_memo) : celui du serveur, sinon un
	// neuf en id 0 — ATTACH_SAVE_MEMO le crée alors et renvoie son id. L'event cible l'attach et non la
	// livraison : le mémo reste donc éditable sur un BL signé, comme sur la page livraison.
	// Méthode d'instance et non statique : l'attach neuf porte l'utilisateur courant.
	memoAttachOf(order: any, deliveryId: number): Attach {
		const del = (order?.lnk_deliveries ?? []).find((d: any) => +d?.id === +deliveryId);
		const found = (del?.lnk_attachs ?? []).find((a: any) => a?.data?.['flags']?.includes('is_default_memo'));
		return found ?? new Attach({
			id: 0,		// 0 => le back crée l'attach au 1er enregistrement
			memo: '',
			name: 'default_memo',
			contenttype: 'text/html',
			data: { flags: ['is_default_memo'] },
			objecttyp: ObjectType.DELIVERY,		// parent de l'attach : la LIVRAISON
			objectid: +deliveryId,
			userid: this.loginService.loggeduser?.id ?? 0,
		});
	}

	// Payload d'event : la tournée + ses livraisons ordonnées (seq_run = 1..n), casts int (selects => strings).
	// Chaque livraison emporte son temps sur place (duration), sa contrainte (lockdatdel) et l'engagement
	// figé à la validation (plandatdel).
	static buildRunPayload(run: DelRun, deliveries: { id: number; duration?: number | null; plandatdel?: string | null; lockdatdel?: string | null }[]): DelRunPayload {
		return {
			id: +run.id,
			seq: +run.seq,
			dat: run.dat,
			slot: run.slot ? +run.slot : null,
			start_slot: run.start_slot ? +run.start_slot : null,
			depot: run.depot ? +run.depot : null,
			vehicle: run.vehicle ? +run.vehicle : null,
			workers: (run.workers ?? []).map((w) => +w),
			lnk_deliveries: (deliveries ?? []).map((d, i) => ({
				id: +d.id,
				seq_run: i + 1,
				duration: (d.duration === null || d.duration === undefined) ? null : +d.duration,
				plandatdel: d.plandatdel ?? null,
				lockdatdel: d.lockdatdel ?? null,
			})),
		};
	}

	// Heure d'un créneau horaire c_values 8 => minutes depuis minuit.
	// Priorité au paramétrage explicite data.start_min (robuste au renommage des créneaux) ;
	// fallback : parsing du nom (« 9h », « 9h30 », « 14h ») pour les lignes non encore migrées.
	static slotTimeOf(slotId: number | null | undefined, slots: C_value[]): number | null {
		const slot = (slots ?? []).find((s: any) => s.id === Number(slotId));
		if (!slot) return null;
		const startMin = Number(slot.data?.start_min);
		if (Number.isFinite(startMin) && startMin > 0) return startMin;
		const m = /^(\d{1,2})\s*h\s*(\d{0,2})/.exec(slot?.name ?? '');
		if (!m) return null;
		return +m[1] * 60 + (+m[2] || 0);
	}

	// --- Adaptateurs référentiel -> entrées de computeSchedule (delrun-schedule.ts) ---
	// Le calcul des heures vit dans le module pur ; ici on ne fait que traduire le domaine
	// (c_values, DeliveryPlanning) en données qu'il comprend.

	// askop (c_values 9) de niveau 2 => nature de la fenêtre. Le niveau 1 (Début / Milieu / Fin de
	// matinée) est absent de la table : c'est une préférence floue, pas une contrainte opposable.
	private static readonly ASKOP_WINDOW_KIND: Record<number, AskWindowKind> = {
		[ASKOP_BEFORE]: 'before',
		[ASKOP_AFTER]: 'after',
		[ASKOP_EXACT]: 'exact',
	};

	// Demande horaire FERME du client => fenêtre exploitable par le calcul, null si la livraison n'en
	// porte pas (créneau vague, ASAP, opérateur de niveau 1, ou créneau dont l'heure est introuvable).
	static askWindowOf(d: DeliveryPlanning, ops: C_value[], slots: C_value[]): AskWindow | null {
		const kind = DelrunsService.ASKOP_WINDOW_KIND[Number(d?.askop)];
		if (!kind) return null;
		const atMin = DelrunsService.slotTimeOf(d.askslot, slots);
		if (atMin === null) return null;
		return { kind, atMin, label: DelrunsService.opAndSlotLabel(d, ops, slots) };
	}

	// Contrainte du planificateur (lockdatdel) => heure MURALE MÉTIER en minutes + signal « autre
	// jour ». Heure et jour sont lus dans le fuseau métier (wallMinutesOf / businessDayOf), jamais
	// dans celui du navigateur : « 2026-07-16T11:00:00+02:00 » vaut 660 min sur tous les postes.
	// On garde l'heure et non l'écart à la date de la tournée : une livraison réaffectée conserve sa
	// contrainte (« 11h exigé » appartient à la livraison), mais sa date devient trompeuse — d'où le
	// drapeau, qui produit un avertissement plutôt qu'une réutilisation silencieuse.
	static lockOf(d: DeliveryPlanning, runDat: string | null | undefined): { min: number | null; otherDay: boolean } {
		const min = wallMinutesOf(d?.lockdatdel);
		if (min === null) return { min: null, otherDay: false };
		const lockDay = businessDayOf(d?.lockdatdel);
		const runDay = runDat ? pickedDateStr(runDat) : null;
		return { min, otherDay: !!runDay && lockDay !== runDay };
	}

	// Adresse complète d'une ligne c_partner pour les panneaux de survol (rien n'est tronqué), avec la
	// structure d'adresse chargée par le service — voir addressCufs.
	addressDetails(p: Partner | null): string[] {
		return DelrunsService.addressDetailsOf(p, this.addressCufs);
	}

	// Adresse complète : toutes les lignes postales, puis les cufs que CETTE adresse porte réellement.
	// Rien n'est nommé en dur : on parcourt la structure déclarée (table c_partner, id_component
	// C_COMPONENT_ADDRESS) et on n'imprime que les champs valués sur cette ligne — une adresse sans cuf
	// ne rend que son libellé postal, exactement comme avant, et un cuf ajouté à l'écran Valeurs
	// apparaît ici sans modification de code.
	static addressDetailsOf(p: Partner | null, cufs: Cuf[] = []): string[] {
		if (!p) return [];
		const lines: string[] = [];
		if (p.street3) lines.push(String(p.street3));		// Appart / Étage / Escalier
		if (p.street2) lines.push(String(p.street2));		// Entrée / Bâtiment / Résidence
		if (p.street1) lines.push(String(p.street1));		// N° et rue
		const zipcity = [p.zip, p.city].filter((v) => !!v).join(' ');
		if (zipcity) lines.push(zipcity);
		const values = (p as any).data?.cufs;
		if (values) for (const cuf of cufs) {
			const line = DelrunsService.cufLineOf(cuf, values[String(cuf.id)]);
			if (line) lines.push(line);
		}
		return lines;
	}

	// « Nom : valeur » d'un cuf, null si le champ n'a rien à dire au livreur (non renseigné, ou clé
	// étrangère dont on n'a que l'id brut — un numéro de tiers n'apprend rien devant une porte).
	private static cufLineOf(cuf: Cuf, raw: any): string | null {
		if (raw === null || raw === undefined || raw === '') return null;
		if (cuf.data?.typ === 'FK') return null;
		const label = (cuf.name ?? '').trim() || String(cuf.id);
		if (cuf.data?.typ === 'BOOLEAN') {
			// Un « non » explicite compte autant qu'un « oui » (« Ascenseur : Non » se prépare) ;
			// seul le non-renseigné est tu.
			const yes = raw === true || raw === 'true';
			return label + ' : ' + (yes ? 'Oui' : 'Non');
		}
		return label + ' : ' + String(raw);
	}

	// Adresse de livraison d'une DeliveryPlanning : lnk_partner_deliv (résolue par le back),
	// sinon l'adresse addr_deliv trouvée dans les adresses du client, sinon le client lui-même.
	static deliveryAddressOf(d: DeliveryPlanning): Partner | null {
		const order = d?.lnk_order;
		if (!order) return null;
		if (order.lnk_partner_deliv) return order.lnk_partner_deliv;
		const addrs = Partner.addresses(order.lnk_customer);	// lignes de rôle -5 de c_partner_partner
		return addrs.find((a: Partner) => a.id === order.addr_deliv) ?? order.lnk_customer ?? null;
	}

	// Coordonnées GPS enregistrées sur une adresse (data.cufs Latitude/Longitude), null si absentes/invalides.
	// Renseignées depuis la fiche client ou automatiquement à la programmation d'une livraison.
	// Le couple (0, 0) compte pour absent (voir GeoService.pointOf) : l'adresse repart au BAN
	// au lieu d'épingler la tournée au large de l'Afrique et de fausser l'itinéraire.
	static coordsOf(p: Partner | null): GeoPoint | null {
		const cufs = (p as any)?.data?.cufs;
		return GeoService.pointOf(cufs?.[CUF_ADDRESS_LAT], cufs?.[CUF_ADDRESS_LNG]);
	}

	static addressLabel(p: Partner | null): string {
		if (!p) return '';
		return [p.street1, p.street3, p.street2, p.zip, p.city].filter((v) => !!v).join(' ');		// street1 = N° et rue en tête
	}

	static customerNameOf(d: DeliveryPlanning): string {
		const c = d?.lnk_order?.lnk_customer;
		return c ? ((c.firstname ?? '') + ' ' + (c.name ?? '')).trim() : '';
	}

	// ============================================================
	// Repères de carte — voir docs/logistic.md
	// ============================================================

	// Rang 1-based => lettre en base 26 bijective : 1 = A, 26 = Z, 27 = AA, 53 = BA.
	static mapRefOfRank(rank: number): string {
		let n = Math.trunc(rank);
		let ref = '';
		while (n > 0) {
			const r = (n - 1) % 26;
			ref = String.fromCharCode(65 + r) + ref;
			n = Math.floor((n - 1) / 26);
		}
		return ref;
	}

	// Repère de carte de chaque livraison de la journée : l'identifiant COURT qui relie une épingle
	// grise à sa vignette. Une LETTRE, jamais un chiffre : elle ne peut se confondre avec aucun n° de
	// commande ni de BL, ce qui interdit de l'utiliser comme référence métier. Éphémère, jamais
	// persistée, elle ne vaut que pour le board du jour.
	//
	// Deux règles portent toute sa sûreté (la fenêtre carte dissociée est un onglet séparé qui peut
	// recalculer de son côté — voir DelrunsSyncService) :
	//   - rang sur la journée ENTIÈRE, jamais sur la vue filtrée : basculer Matin / Après-midi ne
	//     renumérote rien, les lettres affichées ont simplement des trous ;
	//   - tri par id de BL CROISSANT : une vente saisie en cours de session porte l'id le plus haut,
	//     elle AJOUTE une lettre à la fin au lieu de décaler celles déjà lues. Un tri par créneau
	//     insérerait un ASAP en tête et deux fenêtres désynchronisées afficheraient « H » sur deux
	//     clients différents — faux, et silencieux.
	static mapRefsOf(deliveries: { id: number }[]): Map<number, string> {
		const refs = new Map<number, string>();
		[...(deliveries ?? [])]
			.sort((a, b) => a.id - b.id)
			.forEach((d, i) => refs.set(d.id, DelrunsService.mapRefOfRank(i + 1)));
		return refs;
	}
}
